import { ChevronDown, Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { csrfFetch } from "@/lib/csrf-fetch"

export interface EmailPreviewData {
  subject: string
  from: string
  reply_to: string
  html: string
}

// Shared POST-and-parse for both send dialogs' preview endpoints: takes the
// route + body, returns the rendered preview or throws a message the component
// surfaces. Each dialog builds its own body (invoicing vs report params).
export async function requestEmailPreview(
  url: string,
  body: Record<string, unknown>,
): Promise<EmailPreviewData> {
  const res = await csrfFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as EmailPreviewData & {
    error?: string
  }
  if (!res.ok) throw new Error(json.error || "Could not load the preview.")
  return json
}

interface Props {
  // Computed by the parent from data it already has (billing emails /
  // recipient list); the server returns from/reply_to/subject/html.
  to: string
  // The parent owns the endpoint and POST body — this component only knows
  // how to fetch-and-display.
  fetchPreview: () => Promise<EmailPreviewData>
  // Any change refetches (debounced) while the section is expanded — pass a
  // JSON.stringify of everything the preview depends on (message, subject,
  // example recipient).
  refreshKey: string
  // e.g. "Example: Acme Corp — subject varies per customer"
  note?: string
}

// Server-rendered email preview: header rows + the real mailer template's
// HTML in a fully sandboxed iframe (sandbox="" — no scripts, no same-origin;
// which is also why the height is fixed: auto-resizing would require
// allow-same-origin, and that must not be granted to rendered email HTML).
export default function EmailPreview({
  to,
  fetchPreview,
  refreshKey,
  note,
}: Props) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<EmailPreviewData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Guards against out-of-order responses when typing outruns the debounce.
  const requestSeq = useRef(0)

  useEffect(() => {
    // Closing must reset transient state: the trigger's spinner isn't gated on
    // `open`, so an early return here would leave it spinning after a close
    // cancels an in-flight debounce. Clearing data/error too means a reopen
    // starts fresh rather than flashing a previous session's preview.
    if (!open) {
      setLoading(false)
      setData(null)
      setError(null)
      return
    }
    const seq = ++requestSeq.current
    setLoading(true)
    const timer = window.setTimeout(() => {
      fetchPreview()
        .then((d) => {
          if (seq !== requestSeq.current) return
          setData(d)
          setError(null)
        })
        .catch((e) => {
          if (seq !== requestSeq.current) return
          setError(e instanceof Error ? e.message : String(e))
        })
        .finally(() => {
          if (seq === requestSeq.current) setLoading(false)
        })
    }, 400)
    return () => window.clearTimeout(timer)
    // fetchPreview is recreated every parent render; refreshKey is the
    // deliberate dependency contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey])

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1 text-xs font-medium"
        >
          <ChevronDown
            className={
              "size-3.5 transition-transform" + (open ? "" : " -rotate-90")
            }
          />
          Preview email
          {loading && <Loader2 className="ml-1 size-3 animate-spin" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2">
        {note && <p className="text-muted-foreground text-xs italic">{note}</p>}
        {error ? (
          <p className="text-destructive text-xs">{error}</p>
        ) : data ? (
          <div className="rounded-md border">
            <div className="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 px-3 py-2 text-xs">
              <span className="font-medium">From</span>
              <span className="truncate">{data.from}</span>
              <span className="font-medium">Reply-To</span>
              <span className="truncate">{data.reply_to}</span>
              <span className="font-medium">To</span>
              <span className="truncate">{to}</span>
              <span className="font-medium">Subject</span>
              <span className="text-foreground truncate">{data.subject}</span>
            </div>
            <iframe
              sandbox=""
              srcDoc={data.html}
              title="Email preview"
              className="h-64 w-full rounded-b-md border-t bg-white"
            />
          </div>
        ) : loading ? (
          <p className="text-muted-foreground text-xs">Loading preview…</p>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  )
}
